/**
 * What the app remembers a reader chose.
 *
 * Stored in `users.preferences` (a JSON column) for a signed-in reader, so it follows them between
 * devices, and in the browser for one who is not — the reader is public (see the user store).
 * Everything read back is checked against its shape and falls back to its default, so a stored
 * value from an older version, or one edited by hand, can never put the app in a state it cannot
 * draw.
 */

import { DEFAULT_READING_SIZE, READING_SIZES, type ReadingSize } from "@/lib/reader/reading-size"

/**
 * The reader's toolbar, as the reader last left it: every setting in it that describes how they
 * like to read, so the next document opens the same way. Not find or go-to, which are things done
 * rather than ways of reading.
 *
 * One set for every document. The judgment reader and the production reader share what they have
 * in common — the text size, the language, the rail, citation highlighting — and each keeps what is
 * only its own.
 */
export interface ReaderPreferences {
  readingSize: ReadingSize
  /** Which official language version of the text is on screen. */
  textLanguage: "english" | "french"
  /** The production reader's layout: as the document prints, or its structure. */
  layout: "print" | "outline"
  /** Whether the rail is open where there is a column for it. */
  railOpen: boolean
  /** What the production reader's rail shows. */
  railView: "map" | "outline"
  breadcrumb: boolean
  highlights: boolean
  /** The judgment reader's margin: citation counts and the annotation layers. */
  citationNotes: boolean
  contentTags: boolean
  actorTags: boolean
  topicTags: boolean
  propositions: boolean
  /** Whether article commentary tags are drawn over the text. */
  commentary: boolean
}
export const DEFAULT_READER_PREFERENCES: ReaderPreferences = {
  readingSize: DEFAULT_READING_SIZE,
  textLanguage: "english",
  layout: "print",
  railOpen: true,
  railView: "map",
  breadcrumb: true,
  highlights: false,
  citationNotes: false,
  contentTags: false,
  actorTags: false,
  topicTags: false,
  propositions: false,
  commentary: false,
}
export interface UserPreferences {
  reader: ReaderPreferences
}

/**
 * When the reader last cleared their history, as an ISO timestamp, beside `reader` in the same
 * JSON column.
 *
 * Not part of UserPreferences: nothing on the client reads it. It is written by
 * /api/activity/clear and read by the history query, which leaves out every research thread
 * started before it. Research threads are HIDDEN rather than deleted: `research_jobs` is the
 * research service's record of every query, and deleting a job cascades to the bookmarks saved
 * from its answer. The client's preference save merges top-level keys (see
 * /api/user/update-preferences), so saving `reader` leaves this where it is.
 */
export const HISTORY_CLEARED_AT = "history_cleared_at"

export const DEFAULT_PREFERENCES: UserPreferences = { reader: DEFAULT_READER_PREFERENCES }

const oneOf =
  <T extends string | number>(allowed: readonly T[]) =>
  (value: unknown): value is T =>
    allowed.includes(value as T)
const isBoolean = (value: unknown): value is boolean => typeof value === "boolean"

/** How each reader preference is checked on the way in. */
const READER_CHECKS: { [K in keyof ReaderPreferences]: (value: unknown) => value is ReaderPreferences[K] } = {
  readingSize: oneOf(READING_SIZES),
  textLanguage: oneOf(["english", "french"] as const),
  layout: oneOf(["print", "outline"] as const),
  railOpen: isBoolean,
  railView: oneOf(["map", "outline"] as const),
  breadcrumb: isBoolean,
  highlights: isBoolean,
  citationNotes: isBoolean,
  contentTags: isBoolean,
  actorTags: isBoolean,
  topicTags: isBoolean,
  propositions: isBoolean,
  commentary: isBoolean,
}
function readReader(raw: unknown): ReaderPreferences {
  const stored = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const reader = { ...DEFAULT_READER_PREFERENCES }
  for (const key of Object.keys(READER_CHECKS) as (keyof ReaderPreferences)[]) {
    const value = stored[key]
    if (READER_CHECKS[key](value)) (reader as Record<string, unknown>)[key] = value
  }
  return reader
}

/** Stored preferences, as the app can use them: checked, and complete. */
export function readPreferences(raw: unknown): UserPreferences {
  const stored = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  return { reader: readReader(stored.reader) }
}
